import type { Message } from '~/types/messages'

// Discord-style per-user notification settings of a group (groups.proto `GroupNotificationSettings`)
// and the rule that decides whether a message notifies. `shouldNotify` mirrors the backend's push
// rule (PushRecipientPolicy) step for step, so the in-app unread mark/sound and the push agree:
//   1. own message → no (ViewChannels is implied: we only receive channels we can see);
//   2. group, the channel's category or the channel muted (muted_until > now) → no;
//   3. level = channel ?? category ?? group ?? the group's default (admin) ?? ALL_MESSAGES;
//   4. a mention of me = <@me>, a reply to my message, @everyone/@here (unless suppressed), one of
//      my roles (unless suppressed);
//   5. NOTHING → no; ONLY_MENTIONS → only a mention; ALL_MESSAGES → yes, but in a group of more than
//      1000 members only a mention.

export const NotificationLevel = {
  Inherit: 0,
  AllMessages: 1,
  OnlyMentions: 2,
  Nothing: 3,
} as const
export type NotificationLevelValue = typeof NotificationLevel[keyof typeof NotificationLevel]

export const NotificationTargetType = {
  Unspecified: 0,
  Group: 1,
  Category: 2,
  Channel: 3,
} as const
export type NotificationTargetTypeValue = typeof NotificationTargetType[keyof typeof NotificationTargetType]

export const MuteDuration = {
  Unspecified: 0,
  Unmute: 1,
  Minutes15: 2,
  Hours1: 3,
  Hours3: 4,
  Hours8: 5,
  Hours24: 6,
  Forever: 7,
} as const
export type MuteDurationValue = typeof MuteDuration[keyof typeof MuteDuration]

export interface NotificationOverride {
  target_id: string
  target_type: NotificationTargetTypeValue
  level: NotificationLevelValue
  /** RFC3339; null = not muted; year 9999 = forever. */
  muted_until: string | null
}

export interface GroupNotificationSettings {
  group_id: string
  /** Personal group level; INHERIT = the group's default. */
  level: NotificationLevelValue
  muted_until: string | null
  suppress_everyone: boolean
  suppress_roles: boolean
  overrides: NotificationOverride[]
  /** Admin-set group default (ALL_MESSAGES / ONLY_MENTIONS); INHERIT = not set → ALL_MESSAGES. */
  default_level: NotificationLevelValue
  /** More than 1000 members: push only on mentions. */
  large_group: boolean
}

export const LEVEL_LABELS: Record<Exclude<NotificationLevelValue, 0>, string> = {
  [NotificationLevel.AllMessages]: 'Все сообщения',
  [NotificationLevel.OnlyMentions]: 'Только упоминания',
  [NotificationLevel.Nothing]: 'Ничего',
}

export const MUTE_OPTIONS: { value: MuteDurationValue, label: string, ms: number | null }[] = [
  { value: MuteDuration.Minutes15, label: 'На 15 минут', ms: 15 * 60_000 },
  { value: MuteDuration.Hours1, label: 'На 1 час', ms: 60 * 60_000 },
  { value: MuteDuration.Hours3, label: 'На 3 часа', ms: 3 * 60 * 60_000 },
  { value: MuteDuration.Hours8, label: 'На 8 часов', ms: 8 * 60 * 60_000 },
  { value: MuteDuration.Hours24, label: 'На 24 часа', ms: 24 * 60 * 60_000 },
  { value: MuteDuration.Forever, label: 'Навсегда', ms: null },
]

/** What the server stores for "forever" (DateTimeOffset.MaxValue). */
export const FOREVER_ISO = '9999-12-31T23:59:59Z'

// ── Raw payload → settings ────────────────────────────────────────────────────

const LEVEL_NAMES = ['NOTIFICATION_LEVEL_INHERIT', 'NOTIFICATION_LEVEL_ALL_MESSAGES', 'NOTIFICATION_LEVEL_ONLY_MENTIONS', 'NOTIFICATION_LEVEL_NOTHING']
const TARGET_NAMES = ['NOTIFICATION_TARGET_TYPE_UNSPECIFIED', 'NOTIFICATION_TARGET_TYPE_GROUP', 'NOTIFICATION_TARGET_TYPE_CATEGORY', 'NOTIFICATION_TARGET_TYPE_CHANNEL']

function pick(raw: Record<string, unknown>, snake: string): unknown {
  if (snake in raw) return raw[snake]
  const camel = snake.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())
  if (camel in raw) return raw[camel]
  const pascal = camel.charAt(0).toUpperCase() + camel.slice(1)
  return raw[pascal]
}

function enumValue(value: unknown, names: string[], max: number): number {
  let n = 0
  if (typeof value === 'number') n = value
  else if (typeof value === 'string') {
    const upper = value.toUpperCase()
    const index = names.findIndex((name) => name === upper || name.endsWith(`_${upper}`))
    n = index >= 0 ? index : Number(value)
  }
  return Number.isInteger(n) && n >= 0 && n <= max ? n : 0
}

function timestampValue(value: unknown): string | null {
  if (value == null || value === '') return null
  if (typeof value === 'string') return value
  if (typeof value === 'object') {
    const seconds = Number((value as Record<string, unknown>).seconds ?? (value as Record<string, unknown>).Seconds)
    if (Number.isFinite(seconds)) return new Date(seconds * 1000).toISOString()
  }
  return null
}

/**
 * Settings from a command result or a `NotificationSettingsChanged` user event. The event's JSON
 * casing isn't pinned down yet, so snake_case, camelCase and PascalCase keys are all accepted, and
 * enums as numbers or proto names. Null when there's no group id.
 */
export function normalizeNotificationSettings(value: unknown): GroupNotificationSettings | null {
  if (!value || typeof value !== 'object') return null
  const raw = value as Record<string, unknown>
  const groupId = String(pick(raw, 'group_id') ?? '')
  if (!groupId) return null
  const rawOverrides = pick(raw, 'overrides')
  return {
    group_id: groupId,
    level: enumValue(pick(raw, 'level'), LEVEL_NAMES, 3) as NotificationLevelValue,
    muted_until: timestampValue(pick(raw, 'muted_until')),
    suppress_everyone: !!pick(raw, 'suppress_everyone'),
    suppress_roles: !!pick(raw, 'suppress_roles'),
    overrides: (Array.isArray(rawOverrides) ? rawOverrides : [])
      .filter((o): o is Record<string, unknown> => !!o && typeof o === 'object')
      .map((o) => ({
        target_id: String(pick(o, 'target_id') ?? ''),
        target_type: enumValue(pick(o, 'target_type'), TARGET_NAMES, 3) as NotificationTargetTypeValue,
        level: enumValue(pick(o, 'level'), LEVEL_NAMES, 3) as NotificationLevelValue,
        muted_until: timestampValue(pick(o, 'muted_until')),
      }))
      .filter((o) => !!o.target_id),
    default_level: enumValue(pick(raw, 'default_level'), LEVEL_NAMES, 3) as NotificationLevelValue,
    large_group: !!pick(raw, 'large_group'),
  }
}

/** Settings of a group nobody changed yet. */
export function emptyNotificationSettings(groupId: string, defaultLevel: number = NotificationLevel.Inherit): GroupNotificationSettings {
  return {
    group_id: groupId,
    level: NotificationLevel.Inherit,
    muted_until: null,
    suppress_everyone: false,
    suppress_roles: false,
    overrides: [],
    default_level: (defaultLevel || NotificationLevel.Inherit) as NotificationLevelValue,
    large_group: false,
  }
}

// ── Mute deadlines ────────────────────────────────────────────────────────────

export function muteDeadline(mutedUntil: string | null | undefined): number | null {
  if (!mutedUntil) return null
  const at = Date.parse(mutedUntil)
  return Number.isFinite(at) ? at : null
}

export function isMuteActive(mutedUntil: string | null | undefined, now: number): boolean {
  const at = muteDeadline(mutedUntil)
  return at !== null && at > now
}

export function isMuteForever(mutedUntil: string | null | undefined): boolean {
  return !!mutedUntil && new Date(mutedUntil).getUTCFullYear() >= 9999
}

/** The local deadline an optimistic mute gets until the server answers with its own. */
export function localMuteUntil(duration: MuteDurationValue, now: number): string | null {
  if (duration === MuteDuration.Forever) return FOREVER_ISO
  const option = MUTE_OPTIONS.find((o) => o.value === duration)
  return option?.ms ? new Date(now + option.ms).toISOString() : null
}

/** «Заглушено до 14:30», «… до 18.10 14:30» on another day, «Заглушено навсегда». */
export function formatMutedUntil(mutedUntil: string | null | undefined, now: number = Date.now()): string {
  if (isMuteForever(mutedUntil)) return 'Заглушено навсегда'
  const at = muteDeadline(mutedUntil)
  if (at === null) return ''
  const date = new Date(at)
  const time = date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
  if (date.toDateString() === new Date(now).toDateString()) return `Заглушено до ${time}`
  const day = date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })
  return `Заглушено до ${day} ${time}`
}

// ── Resolution ────────────────────────────────────────────────────────────────

export type NotificationSource = 'channel' | 'category' | 'group' | 'default'

export interface NotificationTargetRef {
  type: NotificationTargetTypeValue
  /** The group id for the group row. */
  id: string
  /** For a channel: its category, whose settings it inherits. */
  categoryId?: string | null
}

export interface ResolvedNotification {
  /** This target's own row. */
  ownLevel: NotificationLevelValue
  ownMutedUntil: string | null
  ownMuted: boolean
  /** Muted here or by a parent; `mutedBy` says where (the nearest one). */
  muted: boolean
  mutedBy: NotificationSource | null
  /** Effective level, never INHERIT, and where it came from. */
  level: Exclude<NotificationLevelValue, 0>
  levelSource: NotificationSource
  /** What INHERIT would give here (for «По умолчанию категории (…)»). */
  inheritedLevel: Exclude<NotificationLevelValue, 0>
  inheritedSource: NotificationSource
}

export function findOverride(settings: GroupNotificationSettings | null | undefined, targetId: string | null | undefined): NotificationOverride | null {
  if (!settings || !targetId) return null
  const key = targetId.toLowerCase()
  return settings.overrides.find((o) => o.target_id.toLowerCase() === key) ?? null
}

function defaultLevel(settings: GroupNotificationSettings | null | undefined, fallbackDefault?: number): Exclude<NotificationLevelValue, 0> {
  const level = settings?.default_level || fallbackDefault || NotificationLevel.AllMessages
  return level as Exclude<NotificationLevelValue, 0>
}

export function resolveNotification(
  settings: GroupNotificationSettings | null | undefined,
  target: NotificationTargetRef,
  now: number,
  fallbackDefault?: number,
): ResolvedNotification {
  // Chain from the target up: [own, ...parents], each with its source label.
  const chain: { source: NotificationSource, level: NotificationLevelValue, mutedUntil: string | null }[] = []
  if (target.type === NotificationTargetType.Channel) {
    const own = findOverride(settings, target.id)
    chain.push({ source: 'channel', level: own?.level ?? 0, mutedUntil: own?.muted_until ?? null })
  }
  if (target.type === NotificationTargetType.Channel || target.type === NotificationTargetType.Category) {
    const categoryId = target.type === NotificationTargetType.Category ? target.id : target.categoryId
    const own = findOverride(settings, categoryId)
    chain.push({ source: 'category', level: own?.level ?? 0, mutedUntil: own?.muted_until ?? null })
  }
  chain.push({ source: 'group', level: settings?.level ?? 0, mutedUntil: settings?.muted_until ?? null })

  const first = chain[0]!
  const mutedEntry = chain.find((entry) => isMuteActive(entry.mutedUntil, now))
  const levelEntry = chain.find((entry) => entry.level !== NotificationLevel.Inherit)
  const inheritedEntry = chain.slice(1).find((entry) => entry.level !== NotificationLevel.Inherit)
  const fallback = defaultLevel(settings, fallbackDefault)
  return {
    ownLevel: first.level,
    ownMutedUntil: first.mutedUntil,
    ownMuted: isMuteActive(first.mutedUntil, now),
    muted: !!mutedEntry,
    mutedBy: mutedEntry?.source ?? null,
    level: (levelEntry?.level ?? fallback) as Exclude<NotificationLevelValue, 0>,
    levelSource: levelEntry?.source ?? 'default',
    inheritedLevel: (inheritedEntry?.level ?? fallback) as Exclude<NotificationLevelValue, 0>,
    inheritedSource: inheritedEntry?.source ?? 'default',
  }
}

/** «По умолчанию категории (Все сообщения)» — the radio entry that clears this target's own level. */
export function inheritedLevelLabel(resolved: ResolvedNotification): string {
  const where = resolved.inheritedSource === 'category' ? 'категории' : 'группы'
  return `По умолчанию ${where} (${LEVEL_LABELS[resolved.inheritedLevel]})`
}

// ── Mentions ──────────────────────────────────────────────────────────────────

export interface NotificationViewer {
  userId: string
  roleIds: readonly string[]
}

type MentionFields = Pick<Message, 'content' | 'mentions' | 'mention_roles' | 'mention_everyone' | 'mention_here' | 'reply_to_author_id'>

export function messageMentionsViewer(
  message: MentionFields,
  viewer: NotificationViewer,
  suppress: { everyone: boolean, roles: boolean } = { everyone: false, roles: false },
): boolean {
  const me = viewer.userId.toLowerCase()
  if (!me) return false
  if (message.mentions?.some((u) => u.id.toLowerCase() === me)) return true
  if (message.content?.toLowerCase().includes(`<@${me}>`)) return true
  if (message.reply_to_author_id && message.reply_to_author_id.toLowerCase() === me) return true
  if (!suppress.everyone && (message.mention_everyone || message.mention_here)) return true
  if (!suppress.roles && message.mention_roles?.length) {
    const mine = new Set(viewer.roleIds.map((id) => id.toLowerCase()))
    if (message.mention_roles.some((r) => mine.has(r.id.toLowerCase()))) return true
  }
  return false
}

export function shouldNotify(
  settings: GroupNotificationSettings | null | undefined,
  channel: { id: string, categoryId?: string | null },
  message: MentionFields & { author?: { id?: string } | null },
  viewer: NotificationViewer,
  now: number,
  fallbackDefault?: number,
): boolean {
  if (message.author?.id && message.author.id.toLowerCase() === viewer.userId.toLowerCase()) return false
  const resolved = resolveNotification(settings, { type: NotificationTargetType.Channel, id: channel.id, categoryId: channel.categoryId }, now, fallbackDefault)
  if (resolved.muted) return false
  if (resolved.level === NotificationLevel.Nothing) return false
  const mentioned = messageMentionsViewer(message, viewer, {
    everyone: !!settings?.suppress_everyone,
    roles: !!settings?.suppress_roles,
  })
  if (resolved.level === NotificationLevel.OnlyMentions || settings?.large_group) return mentioned
  return true
}
